import React, { useEffect, useState, useRef } from "react";
import { Modal, Spinner, Form } from "react-bootstrap";
import { Button, TextField, Box, Typography, Backdrop, Fade, CircularProgress, IconButton } from "@material-ui/core";
import { Modal as MuiModal } from "@material-ui/core";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CloseIcon from '@material-ui/icons/Close';
import swal from "sweetalert";
import { config } from "../../../constants";

const STANDARD_BANK_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const NEDBANK_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

// Save session to localStorage
const saveStandardBankSession = (profileId, status, inputPrompt) => {
  const sessionKey = `standardBankSession_${profileId}`;
  localStorage.setItem(sessionKey, JSON.stringify({
    status,
    inputPrompt: inputPrompt || "",
    startedAt: Date.now()
  }));
};

// Load session from localStorage
const loadStandardBankSession = (profileId) => {
  const sessionKey = `standardBankSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    // Check if session is expired (older than 10 minutes)
    if (Date.now() - session.startedAt > STANDARD_BANK_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

// Clear session from localStorage
const clearStandardBankSession = (profileId) => {
  const sessionKey = `standardBankSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// Nedbank localStorage helpers
const saveNedbankSession = (profileId, status, inputPrompt) => {
  const sessionKey = `nedbankSession_${profileId}`;
  localStorage.setItem(sessionKey, JSON.stringify({
    status,
    inputPrompt: inputPrompt || "",
    startedAt: Date.now()
  }));
};

const loadNedbankSession = (profileId) => {
  const sessionKey = `nedbankSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    // Check if session is expired (older than 10 minutes)
    if (Date.now() - session.startedAt > NEDBANK_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

const clearNedbankSession = (profileId) => {
  const sessionKey = `nedbankSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// FNB Session Management
const FNB_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const FNB_POLLING_INTERVAL = 3000; // 3 seconds

const saveFnbSession = (profileId, status, inputPrompt) => {
  const sessionKey = `fnbSession_${profileId}`;
  localStorage.setItem(sessionKey, JSON.stringify({
    status,
    inputPrompt: inputPrompt || "",
    startedAt: Date.now()
  }));
};

const loadFnbSession = (profileId) => {
  const sessionKey = `fnbSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    if (Date.now() - session.startedAt > FNB_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

const clearFnbSession = (profileId) => {
  const sessionKey = `fnbSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// Check if any bank has an active session
const getActiveBankSession = (profileId) => {
  const standardBankSession = loadStandardBankSession(profileId);
  const nedbankSession = loadNedbankSession(profileId);
  const fnbSession = loadFnbSession(profileId);

  // Check if session is active (not terminal state)
  if (standardBankSession && !["approved", "error", "failed"].includes(standardBankSession.status)) {
    return { bank: "Standard Bank", session: standardBankSession };
  }
  if (nedbankSession && !["approved", "error", "failed"].includes(nedbankSession.status)) {
    return { bank: "Nedbank", session: nedbankSession };
  }
  if (fnbSession && !["approved", "failed"].includes(fnbSession.status)) {
    return { bank: "FNB Bank", session: fnbSession };
  }

  return null;
};

const NisaBankModal = ({ show, onHide, autoOpenStatusModal, onStatusModalOpened, onStopPolling }) => {
  const [selectedBank, setSelectedBank] = useState("");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activeSession, setActiveSession] = useState(null);
  // Nedbank status modal states
  const [showNedbankStatusModal, setShowNedbankStatusModal] = useState(false);
  const [nedbankStatus, setNedbankStatus] = useState("initial");
  const [nedbankOtp, setNedbankOtp] = useState("");
  const [nedbankOtpError, setNedbankOtpError] = useState("");
  const [shouldPollNedbank, setShouldPollNedbank] = useState(false);

  // Standard Bank state
  const [showStandardBankStatusModal, setShowStandardBankStatusModal] = useState(false);
  const [standardBankModalStatus, setStandardBankModalStatus] = useState("initial");
  const [standardBankOtp, setStandardBankOtp] = useState("");
  const [standardBankOtpError, setStandardBankOtpError] = useState("");
  const [standardBankInputPrompt, setStandardBankInputPrompt] = useState("");
  const [shouldPollStandardBank, setShouldPollStandardBank] = useState(false);

  // FNB state
  const [showFnbStatusModal, setShowFnbStatusModal] = useState(false);
  const [fnbModalStatus, setFnbModalStatus] = useState("initial");
  const [fnbInputPrompt, setFnbInputPrompt] = useState("");
  const [shouldPollFnb, setShouldPollFnb] = useState(false);

  const POLLING_INTERVAL = 10000; // 10 seconds
  const STANDARD_BANK_POLLING_INTERVAL = 2000; // 2 seconds

  const controllerRef = useRef(new AbortController());

  // Expose stop polling function to parent via ref
  useEffect(() => {
    if (onStopPolling) {
      onStopPolling.current = () => {
        console.log('🛑 [NisaBankModal] Stopping all polling from parent');
        setShouldPollStandardBank(false);
        setShouldPollNedbank(false);
        setShouldPollFnb(false);
      };
    }
  }, [onStopPolling]);

  // Nedbank OTP submission handler
  const handleNedbankOtpSubmit = async () => {
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    if (!nedbankOtp || nedbankOtp.trim().length !== 6) {
      setNedbankOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      // Call API to submit OTP
      console.log("Submitting Nedbank OTP:", nedbankOtp);

      const otpResponse = await fetch(`${config.BANK_API_URL}/api/nedbank/submitOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": config.TENANT_ID
        },
        body: JSON.stringify({
          profileId: profile,
          otp: nedbankOtp.trim()
        })
      });

      if (!otpResponse.ok) {
        const otpData = await otpResponse.json().catch(() => ({}));
        setNedbankOtpError(otpData.error || otpData.message || "Invalid OTP. Please try again.");
        return;
      }

      const otpData = await otpResponse.json();
      console.log("OTP submitted successfully:", otpData);

      // Clear OTP field and error
      setNedbankOtp("");
      setNedbankOtpError("");

      // Update status to show we're waiting - polling will update to actual status
      // Don't set status here, let polling handle it
    } catch (error) {
      console.error("Error submitting OTP:", error);
      setNedbankOtpError("Failed to submit OTP. Please try again.");
    }
  };

  // Nedbank status polling
  const pollNedbankStatus = async (profile) => {
    console.log('🔄 [Nedbank User Module] Polling status for profile:', profile);
    try {
      const res = await fetch(`${config.BANK_API_URL}/api/nedbank/getStatus?profileId=${profile}`, {
        headers: {
          "X-Tenant-ID": config.TENANT_ID
        }
      });

      if (!res.ok) {
        console.error(`🔴 [Nedbank User Module] Status polling failed: ${res.status}`);
        return;
      }

      const data = await res.json();
      const status = data.status;
      const inputPrompt = data.inputPrompt;
      console.log('🔄 [Nedbank User Module] Status response:', { status, inputPrompt });

      // Update status based on API response
      setNedbankStatus(status);

      // Update localStorage with latest status
      saveNedbankSession(profile, status, inputPrompt);

      // Handle terminal states - stop polling
      if (status === "approved" || status === "failed") {
        console.log('🔄 [Nedbank User Module] Terminal state reached:', status);
        setShouldPollNedbank(false);

        // Clear session from localStorage
        clearNedbankSession(profile);
      }
    } catch (error) {
      console.error("🔴 [Nedbank User Module] Error polling status:", error);
    }
  };

  // Standard Bank status polling
  const pollStandardBankStatus = async (profile) => {
    console.log('🔄 [Standard Bank User Module] Polling status for profile:', profile);
    try {
      const res = await fetch(
        `${config.BANK_API_URL}/api/standardBank/getStatus?profileId=${profile}`,
        {
          headers: {
            "X-Tenant-ID": config.TENANT_ID
          }
        }
      );

      if (!res.ok) {
        console.error(`🔴 [Standard Bank User Module] Status polling failed: ${res.status}`);
        return;
      }

      const data = await res.json();
      const { status, inputPrompt } = data;
      console.log('🔄 [Standard Bank User Module] Status response:', { status, inputPrompt });

      setStandardBankModalStatus(status);
      setStandardBankInputPrompt(inputPrompt || "");

      // Update localStorage with latest status
      saveStandardBankSession(profile, status, inputPrompt);

      // Stop polling on terminal states
      if (status === "approved" || status === "error" || status === "failed") {
        console.log('🔄 [Standard Bank User Module] Terminal state reached:', status);
        setShouldPollStandardBank(false);

        // Clear session from localStorage
        clearStandardBankSession(profile);

        // If approved, fetch data
        if (status === "approved") {
          console.log('🔄 [Standard Bank User Module] Fetching final data');
          await fetchStandardBankDataUserModule(profile);
        }
      }
    } catch (error) {
      console.error("🔴 [Standard Bank User Module] Error polling status:", error);
    }
  };

  // Fetch Standard Bank data when approved
  const fetchStandardBankDataUserModule = async (profile) => {
    console.log('📥 [Standard Bank User Module] Fetching final data for profile:', profile);
    try {
      const res = await fetch(
        `${config.BANK_API_URL}/api/standardBank/getData?profileId=${profile}`,
        {
          headers: {
            "X-Tenant-ID": config.TENANT_ID
          }
        }
      );

      if (!res.ok) {
        console.error("🔴 [Standard Bank User Module] Failed to fetch data:", res.status);
        setStandardBankModalStatus("error");
        setStandardBankInputPrompt("Failed to fetch bank data. Please try refreshing.");
        return;
      }

      const data = await res.json();
      console.log('📥 [Standard Bank User Module] Data fetched successfully:', data);

      if (data.success) {
        // Success - data is now available
        // Parent will be notified when modal closes
        console.log('✅ [Standard Bank User Module] Data fetch complete');
      }
    } catch (error) {
      console.error("🔴 [Standard Bank User Module] Error fetching data:", error);
      setStandardBankModalStatus("error");
      setStandardBankInputPrompt("Failed to fetch bank data. Please try refreshing.");
    }
  };

  // Nedbank modal close handler
  const handleNedbankModalClose = () => {
    console.log('🔴 [Nedbank User Module] Modal close requested');
    // Allow closing anytime
    setShowNedbankStatusModal(false);
    // Don't stop polling - continues in background
    // Don't clear localStorage - session persists
  };

  // Standard Bank OTP submit handler (for future)
  const handleStandardBankOtpSubmit = async () => {
    console.log('🟢 [Standard Bank User Module] OTP submit clicked');
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    if (!standardBankOtp || standardBankOtp.trim().length === 0) {
      setStandardBankOtpError("Please enter the OTP");
      return;
    }

    try {
      const response = await fetch(
        `${config.BANK_API_URL}/api/standardBank/submitOtp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-ID": config.TENANT_ID
          },
          body: JSON.stringify({
            profileId: profile,
            otp: standardBankOtp.trim()
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setStandardBankOtpError(
          errorData.message || "Failed to submit OTP. Please try again."
        );
        return;
      }

      console.log('🟢 [Standard Bank User Module] OTP submitted successfully');
      setStandardBankOtp("");
      setStandardBankOtpError("");
      setStandardBankModalStatus("otp-submitted");
      // Polling continues to get real status
    } catch (error) {
      console.error("🔴 [Standard Bank User Module] OTP submission error:", error);
      setStandardBankOtpError(
        error.response?.data?.message ||
        "Failed to submit OTP. Please try again."
      );
    }
  };

  // Standard Bank modal close handler
  const handleStandardBankModalClose = () => {
    console.log('🔴 [Standard Bank User Module] Modal close requested');
    // Allow closing anytime
    setShowStandardBankStatusModal(false);
    // Don't stop polling - continues in background
    // Don't clear localStorage - session persists
  };

  // Standard Bank success handler
  const handleStandardBankSuccess = () => {
    console.log('🟢 [Standard Bank User Module] Success handler called');
    // Just close modal, data already fetched
    handleStandardBankModalClose();
  };

  // Standard Bank try again handler
  const handleStandardBankTryAgain = async () => {
    console.log('🔄 [Standard Bank User Module] Try again clicked');

    try {
      const user = localStorage.getItem("usr");
      const profile = JSON.parse(user)?.profile;

      // Clear session from localStorage
      if (profile) {
        clearStandardBankSession(profile);
      }

      // Reset status on backend
      await fetch(
        `${config.BANK_API_URL}/api/standardBank/resetStatus`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-ID": config.TENANT_ID
          },
          body: JSON.stringify({ profileId: profile })
        }
      );
    } catch (error) {
      console.error("Error resetting status:", error);
      // Continue anyway - let user try again
    }

    // Close status modal and reset
    setShowStandardBankStatusModal(false);
    setShouldPollStandardBank(false);
    setStandardBankModalStatus("initial");
    setStandardBankOtp("");
    setStandardBankOtpError("");
    setStandardBankInputPrompt("");

    // DON'T close the main modal - leave it open for user to retry
    // User can see their previous credentials and try again
  };

  // FNB status polling
  const pollFnbStatus = async (profile) => {
    console.log('🔄 [FNB Bank User Module] Polling status for profile:', profile);
    try {
      const res = await fetch(`${config.BANK_API_URL}/api/fnbBank/getStatus?profileId=${profile}`, {
        headers: {
          "X-Tenant-ID": config.TENANT_ID
        }
      });

      if (!res.ok) {
        console.error(`🔴 [FNB Bank User Module] Status polling failed: ${res.status}`);
        return;
      }

      const data = await res.json();
      const { status, inputPrompt } = data;
      console.log('🔄 [FNB Bank User Module] Status response:', { status, inputPrompt });

      setFnbModalStatus(status);
      setFnbInputPrompt(inputPrompt || "");

      // Update localStorage with latest status
      saveFnbSession(profile, status, inputPrompt);

      // Stop polling on terminal states
      if (status === "approved" || status === "failed") {
        console.log('🔄 [FNB Bank User Module] Terminal state reached:', status);
        setShouldPollFnb(false);

        // Clear session from localStorage
        clearFnbSession(profile);
      }
    } catch (error) {
      console.error("🔴 [FNB Bank User Module] Error polling status:", error);
    }
  };

  // FNB modal close handler
  const handleFnbModalClose = () => {
    console.log('🔴 [FNB Bank User Module] Modal close requested');
    // Allow closing anytime
    setShowFnbStatusModal(false);
    // Don't stop polling - continues in background
    // Don't clear localStorage - session persists
  };

  // FNB try again handler
  const handleFnbTryAgain = async () => {
    console.log('🔄 [FNB Bank User Module] Try again clicked');

    try {
      const user = localStorage.getItem("usr");
      const profile = JSON.parse(user)?.profile;

      // Clear session from localStorage
      if (profile) {
        clearFnbSession(profile);
      }

      // Reset status on backend
      await fetch(
        `${config.BANK_API_URL}/api/fnbBank/resetStatus`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-ID": config.TENANT_ID
          },
          body: JSON.stringify({ profileId: profile })
        }
      );
    } catch (error) {
      console.error("Error resetting status:", error);
    }

    // Close status modal
    handleFnbModalClose();
  };


  // Nedbank polling effect
  useEffect(() => {
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    if (shouldPollNedbank && profile) {
      const intervalId = setInterval(() => pollNedbankStatus(profile), POLLING_INTERVAL);
      return () => clearInterval(intervalId);
    }
  }, [shouldPollNedbank]);

  // Standard Bank polling effect
  useEffect(() => {
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    console.log('⚡ [Standard Bank User Module] useEffect triggered. shouldPoll:', shouldPollStandardBank, 'profile:', profile);
    if (shouldPollStandardBank && profile) {
      console.log('⚡ [Standard Bank User Module] Starting polling interval (every', STANDARD_BANK_POLLING_INTERVAL, 'ms)');

      // Make the first poll immediately
      pollStandardBankStatus(profile);

      // Then set up interval for subsequent polls
      const intervalId = setInterval(
        () => pollStandardBankStatus(profile),
        STANDARD_BANK_POLLING_INTERVAL
      );

      return () => {
        console.log('⚡ [Standard Bank User Module] Cleaning up interval');
        clearInterval(intervalId);
      };
    }
  }, [shouldPollStandardBank]);

  // FNB polling effect
  useEffect(() => {
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    console.log('⚡ [FNB Bank User Module] useEffect triggered. shouldPoll:', shouldPollFnb, 'profile:', profile);
    if (shouldPollFnb && profile) {
      console.log('⚡ [FNB Bank User Module] Starting polling interval (every', FNB_POLLING_INTERVAL, 'ms)');

      // Make the first poll immediately
      pollFnbStatus(profile);

      // Then set up interval for subsequent polls
      const intervalId = setInterval(
        () => pollFnbStatus(profile),
        FNB_POLLING_INTERVAL
      );

      return () => {
        console.log('⚡ [FNB Bank User Module] Cleaning up interval');
        clearInterval(intervalId);
      };
    }
  }, [shouldPollFnb]);

  // Auto-resume Standard Bank session on component mount
  useEffect(() => {
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    if (!profile) return;

    const savedSession = loadStandardBankSession(profile);

    if (savedSession) {
      console.log('🔄 [Standard Bank User Module] Checking saved session:', savedSession);

      // Only resume sessions that are actively in progress
      // DO NOT resume terminal states (failed, error, approved)
      if (["approved", "error", "failed"].includes(savedSession.status)) {
        console.log('🔄 [Standard Bank User Module] Clearing stale terminal state session:', savedSession.status);
        clearStandardBankSession(profile);
        return; // Don't show modal for old terminal states
      }

      // Only resume active/in-progress sessions
      console.log('🔄 [Standard Bank User Module] Resuming active session:', savedSession);
      setStandardBankModalStatus(savedSession.status);
      setStandardBankInputPrompt(savedSession.inputPrompt);
      // Don't auto-open modal - let user click the banner
      // setShowStandardBankStatusModal(true);
      setShouldPollStandardBank(true);
    }
  }, []); // Run only on mount

  // Auto-resume Nedbank session on component mount
  useEffect(() => {
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    if (!profile) return;

    const savedSession = loadNedbankSession(profile);

    if (savedSession) {
      console.log('🔄 [Nedbank User Module] Checking saved session:', savedSession);

      // Only resume sessions that are actively in progress
      if (["approved", "error", "failed"].includes(savedSession.status)) {
        console.log('🔄 [Nedbank User Module] Clearing stale terminal state session:', savedSession.status);
        clearNedbankSession(profile);
        return;
      }

      console.log('🔄 [Nedbank User Module] Resuming active session:', savedSession);
      setNedbankStatus(savedSession.status);
      // Don't auto-open modal - let user click the banner
      // setShowNedbankStatusModal(true);
      setShouldPollNedbank(true);
    }
  }, []); // Run only on mount

  // Auto-resume FNB session on component mount
  useEffect(() => {
    const user = localStorage.getItem("usr");
    const profile = JSON.parse(user)?.profile;

    if (!profile) return;

    const savedSession = loadFnbSession(profile);

    if (savedSession) {
      console.log('🔄 [FNB Bank User Module] Checking saved session:', savedSession);

      // Only resume sessions that are actively in progress
      if (["approved", "failed"].includes(savedSession.status)) {
        console.log('🔄 [FNB Bank User Module] Clearing stale terminal state session:', savedSession.status);
        clearFnbSession(profile);
        return;
      }

      console.log('🔄 [FNB Bank User Module] Resuming active session:', savedSession);
      setFnbModalStatus(savedSession.status);
      setFnbInputPrompt(savedSession.inputPrompt);
      // Don't auto-open modal - let user click the banner
      // setShowFnbStatusModal(true);
      setShouldPollFnb(true);
    }
  }, []); // Run only on mount

  // Check for active sessions on mount and periodically
  useEffect(() => {
    const checkActiveSessions = () => {
      const user = localStorage.getItem("usr");
      const profile = JSON.parse(user)?.profile;
      if (!profile) return;

      const active = getActiveBankSession(profile);
      setActiveSession(active);
    };

    checkActiveSessions();

    // Check every 2 seconds
    const interval = setInterval(checkActiveSessions, 2000);

    return () => clearInterval(interval);
  }, []);

  // Auto-open status modal when triggered from parent (pill click)
  useEffect(() => {
    if (autoOpenStatusModal === "standardBank") {
      console.log('🟢 Auto-opening Standard Bank status modal from pill click');
      setShowStandardBankStatusModal(true);
      if (onStatusModalOpened) {
        onStatusModalOpened();
      }
    } else if (autoOpenStatusModal === "nedbank") {
      console.log('🟢 Auto-opening Nedbank status modal from pill click');
      setShowNedbankStatusModal(true);
      if (onStatusModalOpened) {
        onStatusModalOpened();
      }
    } else if (autoOpenStatusModal === "fnb") {
      console.log('🟢 Auto-opening FNB status modal from pill click');
      setShowFnbStatusModal(true);
      if (onStatusModalOpened) {
        onStatusModalOpened();
      }
    }
  }, [autoOpenStatusModal, onStatusModalOpened]);

  const actionFunction = async () => {
    setLoading(true);

    // Extract profile at function scope with error handling
    let profile;
    try {
      const user = localStorage.getItem("usr");
      profile = JSON.parse(user).profile;
    } catch (error) {
      console.error("Failed to get profile from localStorage:", error);
      swal("Error", "Unable to retrieve user profile. Please log in again.", "error");
      setLoading(false);
      return;
    }

    // Check if another bank session is in progress
    const activeSession = getActiveBankSession(profile);
    if (activeSession) {
      swal(
        "Session in Progress",
        `A session is already in progress for ${activeSession.bank}. Please wait for it to complete before starting another.`,
        "warning"
      );
      setLoading(false);
      return;
    }

    // Create a new AbortController for this login attempt
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    try {
      let url = "";
      let payload = {};

      if (selectedBank === "standard bank") {
        console.log('🔵 [Standard Bank User Module] Starting connection flow');

        // Step 1: Close main modal
        onHide();

        // Step 2: Show status modal
        setShowStandardBankStatusModal(true);
        setStandardBankModalStatus("initial");
        console.log('🔵 [Standard Bank User Module] Status modal opened');

        // Step 3: Save credentials (optional, non-blocking)
        try {
          const saveCredsResponse = await fetch(`${config.BANK_API_URL}/api/credentials/save`, {
            method: "POST",
            body: JSON.stringify({
              profileId: profile,
              email,
              password,
              bankSlug: "standard bank",
            }),
            headers: {
              "Content-Type": "application/json",
              "X-Tenant-ID": config.TENANT_ID
            }
          });

          if (!saveCredsResponse.ok) {
            console.error("Failed to save credentials:", saveCredsResponse.status);
            // Continue anyway - credentials save is not critical
          } else {
            console.log('🔵 [Standard Bank User Module] Credentials saved');
          }
        } catch (error) {
          console.error("Error saving credentials:", error);
          // Continue anyway
        }

        // Step 4: Call login API - ONLY start polling for 2xx responses
        try {
          const response = await fetch(
            `${config.BANK_API_URL}/api/standardBank/login`,
            {
              method: "POST",
              body: JSON.stringify({ profile: profile }),
              headers: {
                "Content-Type": "application/json",
                "X-Tenant-ID": config.TENANT_ID
              }
            }
          );

          console.log('🔵 [Standard Bank User Module] Login API response:', {
            status: response.status,
            statusText: response.statusText
          });

          // Handle 409 - Conflict (session already in progress) - DO NOT poll
          if (response.status === 409) {
            console.log('🔴 [Standard Bank User Module] 409 Conflict - NOT starting polling');
            const errorData = await response.json().catch(() => ({}));
            // Always set to "failed" for 409 - don't use backend status
            setShouldPollStandardBank(false);
            setStandardBankModalStatus("failed");
            setStandardBankInputPrompt(
              errorData.message || "Session already in progress. Please wait and try again later."
            );

            // Clear session from localStorage on error
            clearStandardBankSession(profile);

            setLoading(false);
            return;
          }

          // ONLY success (2xx) starts polling
          if (response.status >= 200 && response.status < 300) {
            console.log('🟢 [Standard Bank User Module] Login successful (2xx) - starting polling');

            // Save session to localStorage
            saveStandardBankSession(profile, "initial", "");

            setShouldPollStandardBank(true);
            setLoading(false);
            return;
          }

          // All other non-2xx responses - don't start polling
          const errorData = await response.json().catch(() => ({}));
          console.error("🔴 [Standard Bank User Module] Login API failed:", errorData);
          // Set failed status FIRST before any other state changes
          setShouldPollStandardBank(false);
          setStandardBankModalStatus("failed");
          setStandardBankInputPrompt(
            errorData.message || "Failed to connect to Standard Bank. Please try again."
          );

          // Clear session from localStorage on error
          clearStandardBankSession(profile);

          setLoading(false);
          return;

        } catch (error) {
          console.error("🔴 [Standard Bank User Module] Error calling login API:", error);

          // Handle 409 in error response - DO NOT poll
          if (error.response?.status === 409) {
            console.log('🔴 [Standard Bank User Module] 409 Conflict in error - NOT starting polling');
            // Always set to "failed" for 409 - don't use backend status
            setShouldPollStandardBank(false);
            setStandardBankModalStatus("failed");
            setStandardBankInputPrompt(
              error.response.data?.message || "Session already in progress. Please wait and try again later."
            );

            // Clear session from localStorage on error
            clearStandardBankSession(profile);

            setLoading(false);
            return;
          }

          // All other errors - don't start polling
          // Set failed status FIRST before any other state changes
          setShouldPollStandardBank(false);
          setStandardBankModalStatus("failed");
          setStandardBankInputPrompt(
            error.response?.data?.message || "Failed to connect to Standard Bank. Please try again."
          );

          // Clear session from localStorage on error
          clearStandardBankSession(profile);

          setLoading(false);
        }

        return; // Exit early for Standard Bank

      } else if (selectedBank === "fnb bank") {
        console.log('🔵 [FNB Bank User Module] Starting connection flow');

        // Step 1: Close main modal
        onHide();

        // Step 2: Show status modal
        setShowFnbStatusModal(true);
        setFnbModalStatus("initial");
        console.log('🔵 [FNB Bank User Module] Status modal opened');

        // Step 3: Save credentials (optional, non-blocking)
        try {
          const saveCredsResponse = await fetch(`${config.BANK_API_URL}/api/credentials/save`, {
            method: "POST",
            body: JSON.stringify({
              profileId: profile,
              email,
              password,
              bankSlug: "fnb bank",
            }),
            headers: {
              "Content-Type": "application/json",
              "X-Tenant-ID": config.TENANT_ID
            }
          });

          if (!saveCredsResponse.ok) {
            console.error("Failed to save credentials:", saveCredsResponse.status);
            // Continue anyway - credentials save is not critical
          } else {
            console.log('🔵 [FNB Bank User Module] Credentials saved');
          }
        } catch (error) {
          console.error("Error saving credentials:", error);
          // Continue anyway
        }

        // Step 4: Call login API - ONLY start polling for 2xx responses
        try {
          const response = await fetch(
            `${config.BANK_API_URL}/api/fnbBank/login`,
            {
              method: "POST",
              body: JSON.stringify({ profile: profile }),
              headers: {
                "Content-Type": "application/json",
                "X-Tenant-ID": config.TENANT_ID
              }
            }
          );

          console.log('🔵 [FNB Bank User Module] Login API response:', {
            status: response.status,
            statusText: response.statusText
          });

          // Handle 409 - Conflict (session already in progress) - DO NOT poll
          if (response.status === 409) {
            console.log('🔴 [FNB Bank User Module] 409 Conflict - NOT starting polling');
            const errorData = await response.json().catch(() => ({}));
            setShouldPollFnb(false);
            setFnbModalStatus("failed");
            setFnbInputPrompt(
              errorData.message || "Session already in progress. Please wait and try again later."
            );

            // Clear session from localStorage on error
            clearFnbSession(profile);

            setLoading(false);
            return;
          }

          // ONLY success (2xx) starts polling
          if (response.status >= 200 && response.status < 300) {
            console.log('🟢 [FNB Bank User Module] Login successful (2xx) - starting polling');

            // Save session to localStorage
            saveFnbSession(profile, "initial", "");

            setShouldPollFnb(true);
            setLoading(false);
            return;
          }

          // All other non-2xx responses - don't start polling
          const errorData = await response.json().catch(() => ({}));
          console.error("🔴 [FNB Bank User Module] Login API failed:", errorData);
          setShouldPollFnb(false);
          setFnbModalStatus("failed");
          setFnbInputPrompt(
            errorData.message || "Failed to connect to FNB Bank. Please try again."
          );

          // Clear session from localStorage on error
          clearFnbSession(profile);

          setLoading(false);
          return;

        } catch (error) {
          console.error("🔴 [FNB Bank User Module] Error calling login API:", error);

          // Handle 409 in error response - DO NOT poll
          if (error.response?.status === 409) {
            console.log('🔴 [FNB Bank User Module] 409 Conflict in error - NOT starting polling');
            setShouldPollFnb(false);
            setFnbModalStatus("failed");
            setFnbInputPrompt(
              error.response.data?.message || "Session already in progress. Please wait and try again later."
            );

            // Clear session from localStorage on error
            clearFnbSession(profile);

            setLoading(false);
            return;
          }

          // All other errors - don't start polling
          setShouldPollFnb(false);
          setFnbModalStatus("failed");
          setFnbInputPrompt(
            error.response?.data?.message || "Failed to connect to FNB Bank. Please try again."
          );

          // Clear session from localStorage on error
          clearFnbSession(profile);

          setLoading(false);
        }

        return; // Exit early for FNB Bank

      } else if (selectedBank === "nedbank") {
        console.log('🔵 [Nedbank User Module] Starting connection flow');

        // Step 1: Close main modal
        onHide();

        // Step 2: Show status modal
        setShowNedbankStatusModal(true);
        setNedbankStatus("initial");
        console.log('🔵 [Nedbank User Module] Status modal opened');

        // Step 3: Save credentials (optional, non-blocking)
        try {
          const saveCredsResponse = await fetch(`${config.BANK_API_URL}/api/credentials/save`, {
            method: "POST",
            body: JSON.stringify({
              profileId: profile,
              email,
              password,
              bankSlug: "nedbank",
            }),
            headers: {
              "Content-Type": "application/json",
              "X-Tenant-ID": config.TENANT_ID
            }
          });

          if (!saveCredsResponse.ok) {
            console.error("Failed to save credentials:", saveCredsResponse.status);
            // Continue anyway - credentials save is not critical
          } else {
            console.log('🔵 [Nedbank User Module] Credentials saved');
          }
        } catch (error) {
          console.error("Error saving credentials:", error);
          // Continue anyway
        }

        // Step 4: Call login API - ONLY start polling for 2xx responses
        try {
          const response = await fetch(
            `${config.BANK_API_URL}/api/nedbank/login`,
            {
              method: "POST",
              body: JSON.stringify({ profile: profile }),
              headers: {
                "Content-Type": "application/json",
                "X-Tenant-ID": config.TENANT_ID
              }
            }
          );

          console.log('🔵 [Nedbank User Module] Login API response:', {
            status: response.status,
            statusText: response.statusText
          });

          // Handle 409 - Conflict (session already in progress) - DO NOT poll
          if (response.status === 409) {
            console.log('🔴 [Nedbank User Module] 409 Conflict - NOT starting polling');
            const errorData = await response.json().catch(() => ({}));
            // Always set to "failed" for 409 - don't use backend status
            setShouldPollNedbank(false);
            setNedbankStatus("failed");

            // Clear session from localStorage on error
            clearNedbankSession(profile);

            setLoading(false);
            return;
          }

          // ONLY success (2xx) starts polling
          if (response.status >= 200 && response.status < 300) {
            console.log('🟢 [Nedbank User Module] Login successful (2xx) - starting polling');

            // Save session to localStorage
            saveNedbankSession(profile, "initial", "");

            setShouldPollNedbank(true);
            setLoading(false);
            return;
          }

          // All other non-2xx responses - don't start polling
          const errorData = await response.json().catch(() => ({}));
          console.error("🔴 [Nedbank User Module] Login API failed:", errorData);
          // Set failed status FIRST before any other state changes
          setShouldPollNedbank(false);
          setNedbankStatus("failed");

          // Clear session from localStorage on error
          clearNedbankSession(profile);

          setLoading(false);
          return;

        } catch (error) {
          console.error("🔴 [Nedbank User Module] Error calling login API:", error);

          // Handle 409 in error response - DO NOT poll
          if (error.response?.status === 409) {
            console.log('🔴 [Nedbank User Module] 409 Conflict in error - NOT starting polling');
            // Always set to "failed" for 409 - don't use backend status
            setShouldPollNedbank(false);
            setNedbankStatus("failed");

            // Clear session from localStorage on error
            clearNedbankSession(profile);

            setLoading(false);
            return;
          }

          // All other errors - don't start polling
          // Set failed status FIRST before any other state changes
          setShouldPollNedbank(false);
          setNedbankStatus("failed");

          // Clear session from localStorage on error
          clearNedbankSession(profile);

          setLoading(false);
        }

        return; // Exit early for Nedbank
      }

      // For FNB Bank and Nedbank - save credentials and call login API
      const saveCredsResponse = await fetch(`${config.BANK_API_URL}/api/credentials/save`, {
        method: "POST",
        body: JSON.stringify({
          profileId: profile,
          email,
          password,
          bankSlug: selectedBank,
        }),
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": config.TENANT_ID
        }
      });

      if (!saveCredsResponse.ok) {
        console.error("Failed to save credentials:", saveCredsResponse.status);
        // Continue anyway - credentials save is not critical
      }

      // Call FNB login - this will wait for the scraping to complete
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
        signal: signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        swal.close();
        setLoading(false);
        setTimeout(() => {
          swal("Oops!", errorData.error || errorData.message || "Something went wrong during verification. Please try again.", "error");
        }, 100);
        return;
      }

      const data = await response.json();
      console.log("Login API completed successfully:", data);

      // API returned success - show success modal
      swal.close();
      setLoading(false);

      setTimeout(() => {
        // Different messages for FNB vs other banks
        const successMessage = selectedBank === "fnb bank"
          ? "Data sync is in progress. You will see in the existing account if it is successful, otherwise you can try again"
          : "Details Added Successfully";

        swal({
          title: "Done",
          text: successMessage,
          icon: "success",
          button: "OK",
        }).then(()=>{
          onHide();
          setSelectedBank("");
          setEmail("");
          setPassword("");
          window.location.reload();
          
        });
      }, 100);

    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.message || "A network error occurred. Please try again later.";
      swal.close(); // Close any loading modals
      setLoading(false);
      setTimeout(() => {
        swal("Oops!", errorMessage, "error");
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Nisa Bank Verification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Session In Progress Banner */}
        {activeSession && (
          <Box
            onClick={() => {
              // Open the respective modal based on which bank has active session
              if (activeSession.bank === "Standard Bank") {
                setShowStandardBankStatusModal(true);
              } else if (activeSession.bank === "Nedbank") {
                setShowNedbankStatusModal(true);
              } else if (activeSession.bank === "FNB Bank") {
                setShowFnbStatusModal(true);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 20px",
              marginBottom: "16px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "20px",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#ffe69c"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#fff3cd"}
          >
            <CircularProgress size={16} style={{ marginRight: 8, color: "#856404" }} />
            <Typography variant="body2" style={{ color: "#856404", fontWeight: 500 }}>
              {activeSession.bank} session in progress - Click to view
            </Typography>
          </Box>
        )}

        <Form.Group controlId="bankSelect">
          <Form.Label>Select Your Bank</Form.Label>
          <Form.Control
            as="select"
            value={selectedBank}
            onChange={(e) => setSelectedBank(e.target.value)}
            disabled={activeSession !== null}
          >
            <option>Select</option>
            <option value="fnb bank">FNB Bank</option>
            <option value="standard bank">Standard Bank</option>
            <option value="nedbank">Nedbank</option>
          </Form.Control>
          {activeSession && (
            <Form.Text className="text-danger">
              Another bank session is in progress. Please wait.
            </Form.Text>
          )}
        </Form.Group>

        {selectedBank && (
          <>
            <Form.Group controlId="emailInput" className="mt-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="passwordInput" className="mt-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Group>
          </>
        )}

        <div className="mt-3 text-center">
          <Button
            variant="contained"
            color="primary"
            onClick={actionFunction}
            disabled={loading}
          >
            {loading ? (
              <Spinner
                animation="border"
                size="sm"
                style={{ color: "white", marginRight: "10px" }}
              />
            ) : null}
            {loading ? "Logging...." : "Submit"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>

    {/* Nedbank Status Modal */}
    <MuiModal
      open={showNedbankStatusModal}
      onClose={() => {}} // Can't close by clicking outside
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{ zIndex: 10000 }}
    >
      <Fade in={showNedbankStatusModal}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: "white",
            borderRadius: 8,
            boxShadow: 24,
            padding: 32,
            outline: "none",
            zIndex: 10001
          }}
        >
          {/* Close Icon */}
          <IconButton
            onClick={handleNedbankModalClose}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: 8,
              color: "#666",
            }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Initial Status */}
          {nedbankStatus === "initial" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                Please wait...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Initializing connection
              </Typography>
            </>
          )}

          {/* Pending Status */}
          {nedbankStatus === "pending" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                Connecting with bank...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Please wait while we establish a secure connection
              </Typography>
            </>
          )}

          {/* Awaiting Approval Status */}
          {nedbankStatus === "awaiting-approval" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                Awaiting Approval
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Please approve from your mobile
              </Typography>
            </>
          )}

          {/* Awaiting OTP Status */}
          {nedbankStatus === "awaiting-otp" && (
            <>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                Enter OTP
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 16, textAlign: "center", color: "#666" }}>
                Please enter the 6-digit OTP sent to your mobile
              </Typography>
              <TextField
                fullWidth
                type="text"
                label="OTP"
                value={nedbankOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setNedbankOtp(value);
                  setNedbankOtpError("");
                }}
                error={!!nedbankOtpError}
                helperText={nedbankOtpError}
                autoFocus
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 }
                }}
              />
              <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNedbankOtpSubmit}
                  disabled={nedbankOtp.length !== 6}
                >
                  Submit
                </Button>
              </Box>
            </>
          )}

          {/* OTP Submitted Status */}
          {nedbankStatus === "otp-submitted" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                OTP Submitted
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Connecting with bank...
              </Typography>
            </>
          )}

          {/* Authenticated Status */}
          {nedbankStatus === "authenticated" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                Fetching data
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 16 }}>
                You can close now and come back later or wait because this can take 5 to 10 minutes.
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  onClick={handleNedbankModalClose}
                >
                  Close
                </Button>
              </Box>
            </>
          )}

          {/* Approved Status */}
          {nedbankStatus === "approved" && (
            <>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "green" }}>
                Success!
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                Data fetched successfully
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleNedbankModalClose();
                    window.location.reload();
                  }}
                >
                  Done
                </Button>
              </Box>
            </>
          )}

          {/* Failed Status */}
          {nedbankStatus === "failed" && (
            <>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "red" }}>
                Failed
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                Something went wrong. Please try again in some time
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNedbankModalClose}
                >
                  Done
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Fade>
    </MuiModal>

    {/* Standard Bank Status Modal */}
    <MuiModal
      open={showStandardBankStatusModal}
      onClose={() => {
        // Only close on allowed states
        if (["authenticated", "approved", "error", "failed"].includes(standardBankModalStatus)) {
          handleStandardBankModalClose();
        }
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{ zIndex: 10000 }}
    >
      <Fade in={showStandardBankStatusModal}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: "white",
            borderRadius: 8,
            boxShadow: 24,
            padding: 32,
            outline: "none",
            zIndex: 10001
          }}
        >
          {/* Close Icon */}
          <IconButton
            onClick={handleStandardBankModalClose}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: 8,
              color: "#666",
            }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Initial State */}
          {standardBankModalStatus === "initial" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                Please wait...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Initializing connection
              </Typography>
            </>
          )}

          {/* Pending State */}
          {standardBankModalStatus === "pending" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                Logging in to Standard Bank...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Please wait while we establish a secure connection
              </Typography>
            </>
          )}

          {/* Awaiting Approval State */}
          {standardBankModalStatus === "awaiting-approval" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                Awaiting Approval
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                {standardBankInputPrompt || "Please approve the login request from your mobile device"}
              </Typography>
            </>
          )}

          {/* Awaiting OTP State (for future) */}
          {standardBankModalStatus === "awaiting-otp" && (
            <>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                Enter OTP
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 16, textAlign: "center", color: "#666" }}>
                {standardBankInputPrompt || "Please enter the OTP sent to your mobile"}
              </Typography>
              <TextField
                fullWidth
                type="text"
                label="OTP"
                value={standardBankOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setStandardBankOtp(value);
                  setStandardBankOtpError("");
                }}
                error={!!standardBankOtpError}
                helperText={standardBankOtpError}
                autoFocus
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 }
                }}
              />
              <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStandardBankOtpSubmit}
                  disabled={standardBankOtp.length === 0}
                >
                  Submit OTP
                </Button>
              </Box>
            </>
          )}

          {/* OTP Submitted State */}
          {standardBankModalStatus === "otp-submitted" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                Verifying OTP...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Please wait
              </Typography>
            </>
          )}

          {/* Authenticated State */}
          {standardBankModalStatus === "authenticated" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center" }}>
                Retrieving transactions...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 16 }}>
                This may take a few moments
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  onClick={handleStandardBankModalClose}
                >
                  Close and wait
                </Button>
              </Box>
            </>
          )}

          {/* Approved State */}
          {standardBankModalStatus === "approved" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CheckCircleIcon style={{ fontSize: 60, color: "#4caf50" }} />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#4caf50" }}>
                Success!
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                Bank connected successfully
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#4caf50", color: "white" }}
                  onClick={() => {
                    handleStandardBankSuccess();
                    window.location.reload();
                  }}
                >
                  Done
                </Button>
              </Box>
            </>
          )}

          {/* Error State */}
          {standardBankModalStatus === "error" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <ErrorIcon style={{ fontSize: 60, color: "#f44336" }} />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#f44336" }}>
                Error
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                {standardBankInputPrompt || "Something went wrong. Please try again later."}
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#f44336", color: "white" }}
                  onClick={handleStandardBankTryAgain}
                >
                  OK
                </Button>
              </Box>
            </>
          )}

          {/* Failed State */}
          {standardBankModalStatus === "failed" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <ErrorIcon style={{ fontSize: 60, color: "#f44336" }} />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#f44336" }}>
                Authentication Failed
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                {standardBankInputPrompt ||
                 "Invalid credentials. Please check your username and password."}
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#f44336", color: "white" }}
                  onClick={handleStandardBankTryAgain}
                >
                  OK
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Fade>
    </MuiModal>

    {/* FNB Status Modal */}
    <MuiModal
      open={showFnbStatusModal}
      onClose={() => {
        // Only allow closing on terminal states
        if (["approved", "failed"].includes(fnbModalStatus)) {
          handleFnbModalClose();
        }
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{ zIndex: 10000 }}
    >
      <Fade in={showFnbStatusModal}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: "white",
            borderRadius: 8,
            boxShadow: 24,
            padding: 32,
            outline: "none",
            zIndex: 10001
          }}
        >
          {/* Close Icon */}
          <IconButton
            onClick={handleFnbModalClose}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: 8,
              color: "#666",
            }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Initial Status */}
          {fnbModalStatus === "initial" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                Please wait...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                Initializing connection with FNB Bank
              </Typography>
            </>
          )}

          {/* In Progress Status */}
          {fnbModalStatus === "in-progress" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CircularProgress />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 8, textAlign: "center" }}>
                Scraping FNB data...
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666" }}>
                {fnbInputPrompt || "Please wait while we fetch your bank data"}
              </Typography>
            </>
          )}

          {/* Approved Status */}
          {fnbModalStatus === "approved" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <CheckCircleIcon style={{ fontSize: 60, color: "#4caf50" }} />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#4caf50" }}>
                Success!
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                Bank data fetched successfully
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#4caf50", color: "white" }}
                  onClick={handleFnbModalClose}
                >
                  Done
                </Button>
              </Box>
            </>
          )}

          {/* Failed Status */}
          {fnbModalStatus === "failed" && (
            <>
              <Box style={{ textAlign: "center", marginBottom: 16 }}>
                <ErrorIcon style={{ fontSize: 60, color: "#f44336" }} />
              </Box>
              <Typography variant="h6" style={{ marginBottom: 16, textAlign: "center", color: "#f44336" }}>
                Authentication Failed
              </Typography>
              <Typography variant="body2" style={{ textAlign: "center", color: "#666", marginBottom: 24 }}>
                {fnbInputPrompt || "Something went wrong. Please try again later."}
              </Typography>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#f44336", color: "white" }}
                  onClick={handleFnbTryAgain}
                >
                  OK
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Fade>
    </MuiModal>
    </>
  );
};

export default NisaBankModal;
